import {Box, Image, Text, useMediaQuery} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { FiCheck } from "react-icons/fi";
import { RiCloseLine } from "react-icons/ri";
import "../../../App.css";
import APButton from "../../../components/APButton";
import APDialog from "../../../components/APDialog";
import { ValidationStatusModel, MathProblem } from "../../../types";
import {getFormattedProblem, intlNumber} from "../../../utils";
import {FormattedMessage, useIntl} from "react-intl";

interface ValidationStatusProps {
  onNext: (value: string) => void;
  status: ValidationStatusModel | undefined;
  correctAnswer: string;
}

export default function ValidationStatus(props: ValidationStatusProps) {
  const { onNext, status, correctAnswer } = props;
  const intl = useIntl();
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");

  const handleNext = React.useCallback(() => {
    if (!!status) {
      onNext(status);
    }
  }, [status, onNext]);

  const description = React.useMemo(
    () =>
      status === "success" ? (
        <Box sx={{ ml: isLargeScreen ? "30px" : 0, mr: isLargeScreen ? "120px" : 0 }}>
          <Text sx={{
            fontSize: isLargeScreen ? "26px" : "21px",
            fontWeight: 700,
            w: !isLargeScreen && "100%",
            textAlign: !isLargeScreen && "center",
            mb: !isLargeScreen && "40px"
          }}>
            <FormattedMessage id="correct-answer-label" defaultMessage={"Bonne réponse! Bravo!"}/>
          </Text>
        </Box>
      ) : (
        <Box sx={{ ml: isLargeScreen ? "30px" : 0, mr: isLargeScreen ? "120px" : 0 }}>
          <Text sx={{
            fontSize: isLargeScreen ? "26px" : "21px",
            fontWeight: 700,
            w: !isLargeScreen && "100%",
            textAlign: !isLargeScreen && "center"
          }}>
            <FormattedMessage id="incorrect-answer-label" defaultMessage={"Oups! Ce n’est pas la bonne réponse."}/>
          </Text>
          <Box display="flex" sx={{ justifyContent: !isLargeScreen && "center", my: !isLargeScreen && "35px" }}>
            <Text sx={{ fontSize: isLargeScreen ? "18px" : "14px", fontWeight: 700, mr: 1 }}>
              <FormattedMessage id="incorrect-answer-correct-word" defaultMessage={"La bonne réponse est : "}/>
            </Text>
            <Text sx={{ fontSize: isLargeScreen ? "18px" : "14px", fontWeight: 700 }}>
              {intlNumber(parseFloat(correctAnswer), intl.locale)}.
            </Text>
          </Box>
        </Box>
      ),
    [status]
  );

  const imageURL = useMemo(() => {
    return status === "success"
      ? `${process.env.REACT_APP_BASE_URL}congs-success.png`
      : status !== undefined
      ? `${process.env.REACT_APP_BASE_URL}congs-error.png`
      : "";
  }, [status]);

  const statusIcon = useMemo(() => {
    return status === "success" ? <FiCheck /> : <RiCloseLine />;
  }, [status]);

  const statusBarColor = useMemo(() => {
    return status === "success" ? "primary" : "error";
  }, [status]);

  return (
    <Box
      sx={{
        position: "absolute",
        height: isLargeScreen && "202px",
        width: "100%",
        bottom: 0,
        background: "white",
        zIndex: 10,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: isLargeScreen ? "10px" : "8px",
          bgColor: statusBarColor,
          mb: !isLargeScreen && "24px"
        }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          flexDirection: isLargeScreen ? "row" : "column",
          px: !isLargeScreen && "24px"
        }}
      >
        {isLargeScreen && <Image src={imageURL} sx={{w: isLargeScreen ? "120px" : "96px"}} />}
        {description}
        <APButton
          colorScheme={"primary"}
          sx={{
            w: !isLargeScreen && "100%",
            mb: !isLargeScreen && "40px",
            fontSize: isLargeScreen ? "1.26rem" : "1rem",
            height: isLargeScreen ? "50px" : "40px"
          }}
          onClick={handleNext}
          autoFocus
        >
          <FormattedMessage id="next-button" defaultMessage={"Suivant"}/>
        </APButton>
      </Box>
    </Box>
  );
}
